import React, { useCallback, useState } from "react";

import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Collapse,
    FormControl,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";

import { ClipLoader } from "react-spinners";

import { getB24Url } from "helpers/Sources/b24/b24Source";
import { getBigqueryUrl } from "helpers/Sources/bigquery/bigquerySource";
import { getHubspotUrl, getZohoReconnect } from "helpers/Sources/createSource";
import { sourceGoogleSheets } from "helpers/Sources/googleSheets";
import { getMondayURL } from "helpers/Sources/monday/monday";
import { withHttpsScheme } from "helpers/Sources/odoo/odoo";
import { getPipedriveUrl } from "helpers/Sources/pipedrive/pipedriveSource";
import { getZendeskUrl } from "helpers/Sources/zendesk/zendeskSource";

import LoginButton from "components/molecules/LoginButton";

import { useSourceReconnect } from "../../../hooks/useSourceReconnect";
import { SourceCode, TabProps } from "../../../SourceModal.types";

const PLATFORM_OPTIONS = ["android", "ios", "windows"];

interface FormField {
    name: string;
    label: string;
    type?: "text" | "password" | "select";
    options?: string[];
    placeholder?: string;
    normalize?: (value: string) => string;
}

type SourceFormConfig = {
    fields: FormField[];
    buildPayload: (values: Record<string, string>) => any;
};

const FORM_CONFIGS: Partial<Record<SourceCode, SourceFormConfig>> = {
    ftp: {
        fields: [
            { name: "domain", label: "Host", placeholder: "Host" },
            { name: "username", label: "Username", placeholder: "Username" },
            {
                name: "password",
                label: "Password",
                placeholder: "Password",
                type: "password",
            },
            {
                name: "type",
                label: "Read Type",
                type: "select",
                options: ["file", "directory"],
            },
            {
                name: "transfer_mode",
                label: "Transfer Mode",
                type: "select",
                options: ["default", "active", "passive"],
            },
        ],
        buildPayload: (values) => ({
            source_type: "ftp",
            ...values,
            port: "21",
            path: "/",
        }),
    },
    sftp: {
        fields: [
            { name: "domain", label: "Host", placeholder: "Host" },
            { name: "username", label: "Username", placeholder: "Username" },
            {
                name: "password",
                label: "Password",
                placeholder: "Password",
                type: "password",
            },
            {
                name: "type",
                label: "Read Type",
                type: "select",
                options: ["file", "directory"],
            },
        ],
        buildPayload: (values) => ({
            source_type: "sftp",
            ...values,
            port: "22",
            path: "/",
        }),
    },
    freshsales: {
        fields: [
            {
                name: "freshsales_api_key",
                label: "API Key",
                placeholder: "API Key",
            },
            {
                name: "freshsales_domain",
                label: "Bundle Alias",
                placeholder: "Bundle Alias",
            },
        ],
        buildPayload: (values) => values,
    },
    vtiger: {
        fields: [
            {
                name: "crm_username",
                label: "Username",
                placeholder: "Username",
            },
            {
                name: "crm_access_key",
                label: "Access Key",
                placeholder: "Access Key",
            },
            { name: "crm_url", label: "URL", placeholder: "URL" },
        ],
        buildPayload: (values) => values,
    },
    odoo: {
        fields: [
            {
                name: "odoo_base_url",
                label: "Base URL",
                placeholder: "Base URL",
                normalize: withHttpsScheme,
            },
            {
                name: "odoo_database",
                label: "Database",
                placeholder: "Database",
            },
            {
                name: "odoo_username",
                label: "Username",
                placeholder: "Username",
            },
            { name: "odoo_token", label: "Token", placeholder: "Token" },
        ],
        buildPayload: (values) => values,
    },
    lsqrd: {
        fields: [
            {
                name: "leadsquared_crm_domain",
                label: "Domain",
                placeholder: "Domain",
            },
            {
                name: "leadsquared_access_key",
                label: "Access Key",
                placeholder: "Access Key",
            },
            {
                name: "leadsquared_secret_key",
                label: "Secret Key",
                placeholder: "Secret Key",
            },
        ],
        buildPayload: (values) => ({
            crm_domain: values.leadsquared_crm_domain,
            accessKey: values.leadsquared_access_key,
            secretKey: values.leadsquared_secret_key,
        }),
    },
    appsflyer: {
        fields: [
            { name: "api_key", label: "API Key", placeholder: "API Key" },
            { name: "app_id", label: "APP ID", placeholder: "APP ID" },
            {
                name: "platform",
                label: "Platform",
                type: "select",
                options: PLATFORM_OPTIONS,
            },
        ],
        buildPayload: (values) => values,
    },
    adjust: {
        fields: [
            { name: "api_key", label: "API Key", placeholder: "API Key" },
            { name: "app_id", label: "APP ID", placeholder: "APP ID" },
            {
                name: "platform",
                label: "Platform",
                type: "select",
                options: PLATFORM_OPTIONS,
            },
        ],
        buildPayload: (values) => values,
    },
};

const FormReconnect: React.FC<{
    config: SourceFormConfig;
    isLoading: boolean;
    onSubmit: (data: any) => Promise<void>;
    language: TabProps["language"];
}> = ({ config, isLoading, onSubmit, language }) => {
    const initialValues: Record<string, string> = {};
    config.fields.forEach((f) => {
        initialValues[f.name] = "";
    });
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleChange = (name: string, value: string) => {
        setValues((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({
            ...prev,
            [name]: value
                ? ""
                : (language?.tracker_wizard?.cannot_be_blank ??
                  "Cannot be blank!"),
        }));
    };

    const handleSubmit = () => {
        const normalizedValues = { ...values };
        config.fields.forEach((field) => {
            if (field.normalize) {
                normalizedValues[field.name] = field.normalize(
                    values[field.name] ?? "",
                );
            }
        });
        setValues(normalizedValues);

        const newErrors: Record<string, string> = {};
        config.fields.forEach((f) => {
            if (!normalizedValues[f.name]) {
                newErrors[f.name] =
                    language?.tracker_wizard?.cannot_be_blank ??
                    "Cannot be blank!";
            }
        });
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const payload = config.buildPayload(normalizedValues);
        onSubmit(payload);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {config.fields.map((field) =>
                field.type === "select" ? (
                    <TextField
                        key={field.name}
                        select
                        label={field.label}
                        value={values[field.name]}
                        onChange={(e) =>
                            handleChange(field.name, e.target.value)
                        }
                        error={!!errors[field.name]}
                        helperText={errors[field.name]}
                        fullWidth
                    >
                        {field.options?.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : (
                    <TextField
                        key={field.name}
                        label={field.label}
                        placeholder={field.placeholder}
                        type={field.type || "text"}
                        value={values[field.name]}
                        onChange={(e) =>
                            handleChange(field.name, e.target.value)
                        }
                        error={!!errors[field.name]}
                        helperText={errors[field.name]}
                        fullWidth
                    />
                ),
            )}
            <Box
                sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignSelf: "flex-start",
                    justifyContent: "flex-end",
                    width: "100%",
                }}
            >
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    startIcon={
                        isLoading ? <CircularProgress size={17} /> : undefined
                    }
                >
                    {isLoading ? "Reconnecting..." : "Reconnect"}
                </Button>
            </Box>
        </Box>
    );
};

const ReconnectTab: React.FC<TabProps> = ({ source, language, onRefresh }) => {
    const sourceCode = source?.source_type?.code;
    const [selectedHubspotModule, setSelectedHubspotModule] = useState<{
        label: string;
        value: string;
    } | null>(null);

    const {
        isLoading,
        setIsLoading,
        reconnectWithData,
        reconnectFtp,
        reconnectZendesk,
        reconnectPipedrive,
        reconnectB24,
        reconnectBigquery,
        reconnectGoogleSheets,
    } = useSourceReconnect({
        source,
        language,
        onReconnectSuccess: onRefresh,
    });

    const handleZohoSuccess = useCallback(
        (data: any) => {
            if (data?.code) {
                reconnectWithData({
                    code: data.code,
                    location: data.location,
                    account_server: data["accounts-server"],
                    module: "contacts",
                    source_type: "zoho",
                    name: source?.name,
                });
            }
        },
        [reconnectWithData, source?.name],
    );

    const handleHubspotSuccess = useCallback(
        (data: any) => {
            if (data?.code) {
                reconnectWithData({
                    code: data.code,
                    module: selectedHubspotModule?.value || "contacts",
                });
            }
        },
        [reconnectWithData, selectedHubspotModule],
    );

    const handleZendeskSuccess = useCallback(
        (data: any) => {
            if (data?.code) {
                reconnectZendesk({ code: data.code, state: data.state });
            }
        },
        [reconnectZendesk],
    );

    const handlePipedriveSuccess = useCallback(
        (data: any) => {
            if (data?.code) {
                reconnectPipedrive({ code: data.code, state: data.state });
            }
        },
        [reconnectPipedrive],
    );

    const handleB24Success = useCallback(
        (data: any) => {
            if (data?.code) {
                reconnectB24({ code: data.code });
            }
        },
        [reconnectB24],
    );

    const handleBigquerySuccess = useCallback(
        (data: any) => {
            if (data?.code) {
                reconnectBigquery({ code: data.code });
            }
        },
        [reconnectBigquery],
    );

    const handleMondaySuccess = useCallback(
        (data: any) => {
            if (data?.code) {
                reconnectWithData({ code: data.code, state: data.state });
            }
        },
        [reconnectWithData],
    );

    const handleSpreadsheetSuccess = useCallback(
        (data: any) => {
            if (data?.code) {
                reconnectGoogleSheets({ google_code: data.code });
            }
        },
        [reconnectGoogleSheets],
    );

    const renderOAuthReconnect = () => {
        switch (sourceCode) {
            case "zoho":
                return (
                    <Box sx={{ position: "relative" }}>
                        <LoginButton
                            getAuthUrl={() => getZohoReconnect(source.id)}
                            getAuthUrlResponseTransform={(response: any) =>
                                response?.data?.url
                            }
                            code="zoho"
                            paramNames={["code", "location", "accounts-server"]}
                            onSuccess={handleZohoSuccess}
                            logoSrc="/assets/images/zohoLogo.png"
                            buttonText="Connect with Zoho"
                            logoWidth={60}
                            isLoadingExternal={isLoading}
                        />
                    </Box>
                );

            case "hub":
                return (
                    <Box sx={{ position: "relative" }}>
                        <FormControl sx={{ width: "100%", mb: 2 }}>
                            <Autocomplete
                                onChange={(_e, value) =>
                                    setSelectedHubspotModule(value)
                                }
                                value={selectedHubspotModule}
                                options={[
                                    { label: "Contacts", value: "contacts" },
                                    { label: "Leads", value: "leads" },
                                ]}
                                getOptionLabel={(option) => option?.label ?? ""}
                                isOptionEqualToValue={(option, val) =>
                                    option.value === val.value
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={"Choose a module"}
                                    />
                                )}
                            />
                        </FormControl>
                        <LoginButton
                            getAuthUrl={() =>
                                getHubspotUrl(
                                    selectedHubspotModule?.value || "",
                                )
                            }
                            getAuthUrlResponseTransform={(response) =>
                                response?.data?.url
                            }
                            code="hubspot"
                            paramNames={["code"]}
                            onSuccess={handleHubspotSuccess}
                            isLoadingExternal={isLoading}
                            logoSrc="/assets/images/typeImages/hubspot-icon.png"
                            buttonText="Connect with HubSpot"
                        />
                    </Box>
                );

            case "zendesk_sell":
                return (
                    <Box sx={{ position: "relative" }}>
                        <LoginButton
                            getAuthUrl={getZendeskUrl}
                            getAuthUrlResponseTransform={(response) =>
                                response?.data?.auth_url
                            }
                            code="zendesk_sell"
                            paramNames={["code", "state"]}
                            onSuccess={handleZendeskSuccess}
                            isLoadingExternal={isLoading}
                            logoSrc="/assets/images/typeImages/zendesk-icon.png"
                            buttonText="Connect with Zendesk"
                        />
                    </Box>
                );

            case "pipe":
                return (
                    <Box sx={{ position: "relative" }}>
                        <LoginButton
                            getAuthUrl={getPipedriveUrl}
                            getAuthUrlResponseTransform={(response) =>
                                response?.data?.auth_url
                            }
                            code="pipedrive"
                            paramNames={["code", "state"]}
                            onSuccess={handlePipedriveSuccess}
                            isLoadingExternal={isLoading}
                            logoSrc="/assets/images/typeImages/pipedrive.png"
                            buttonText="Connect with Pipedrive"
                        />
                    </Box>
                );

            case "b24":
                return (
                    <Box sx={{ position: "relative" }}>
                        <LoginButton
                            getAuthUrl={getB24Url}
                            getAuthUrlResponseTransform={(response) =>
                                response?.data?.auth_url
                            }
                            code="b24"
                            paramNames={["code"]}
                            onSuccess={handleB24Success}
                            isLoadingExternal={isLoading}
                            logoSrc="/assets/images/typeImages/b24.png"
                            buttonText="Connect with Bitrix24"
                        />
                    </Box>
                );

            case "bigquery":
                return (
                    <Box sx={{ position: "relative" }}>
                        <LoginButton
                            getAuthUrl={getBigqueryUrl}
                            getAuthUrlResponseTransform={(response) =>
                                response?.data?.auth_url
                            }
                            code="bigquery"
                            paramNames={["code"]}
                            onSuccess={handleBigquerySuccess}
                            logoSrc="/assets/images/typeImages/bigquery.png"
                            buttonText="Connect with BigQuery"
                            isLoadingExternal={isLoading}
                        />
                    </Box>
                );

            case "monday":
                return (
                    <Box sx={{ position: "relative" }}>
                        <LoginButton
                            getAuthUrl={getMondayURL}
                            getAuthUrlResponseTransform={(response) =>
                                response?.data?.auth_url
                            }
                            code="monday"
                            paramNames={["code", "state"]}
                            onSuccess={handleMondaySuccess}
                            isLoadingExternal={isLoading}
                            logoSrc="/assets/images/typeImages/monday.png"
                            buttonText="Connect with Monday"
                        />
                    </Box>
                );

            case "spreadsheet":
                return (
                    <Box sx={{ position: "relative" }}>
                        <LoginButton
                            getAuthUrl={sourceGoogleSheets}
                            getAuthUrlData={{ type_name: "google_spreadsheet" }}
                            getAuthUrlResponseTransform={(response) =>
                                response?.data?.url
                            }
                            code="google-sheets"
                            paramNames={["code"]}
                            onSuccess={handleSpreadsheetSuccess}
                            isLoadingExternal={isLoading}
                            logoSrc="/assets/images/typeImages/google-sheets-icon.png"
                            buttonText="Connect with Google Sheets"
                        />
                    </Box>
                );

            default:
                return null;
        }
    };

    const renderFormReconnect = () => {
        const formConfig = FORM_CONFIGS[sourceCode as SourceCode];
        if (!formConfig) return null;

        const submitHandler =
            sourceCode === "ftp" || sourceCode === "sftp"
                ? reconnectFtp
                : reconnectWithData;

        return (
            <FormReconnect
                config={formConfig}
                isLoading={isLoading}
                onSubmit={submitHandler}
                language={language}
            />
        );
    };

    const OAUTH_SOURCES: SourceCode[] = [
        "zoho",
        "hub",
        "zendesk_sell",
        "pipe",
        "b24",
        "bigquery",
        "monday",
        "spreadsheet",
    ];

    const FORM_SOURCES: SourceCode[] = [
        "ftp",
        "sftp",
        "freshsales",
        "vtiger",
        "odoo",
        "lsqrd",
        "appsflyer",
        "adjust",
    ];

    const isOAuth = OAUTH_SOURCES.includes(sourceCode as SourceCode);
    const isForm = FORM_SOURCES.includes(sourceCode as SourceCode);

    const isDisconnected = source?.status === 0;

    return (
        <div>
            <Collapse in={isDisconnected}>
                <Alert severity="warning" className="mb-3 border-radius-2">
                    <AlertTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
                        Connection Lost
                    </AlertTitle>
                    Since the Access Token is expired, you must reconnect the
                    source to resume data synchronization.
                </Alert>
            </Collapse>

            <Typography variant="h5" fontWeight="bold" gutterBottom>
                {language?.buttons?.reconnect ?? "Reconnect"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Re-establishes the connection with the source to ensure ongoing
                data transfer and resolve any connectivity issues.
            </Typography>

            <Box>
                {isOAuth && renderOAuthReconnect()}
                {isForm && renderFormReconnect()}
                {!isOAuth && !isForm && (
                    <Typography color="text.secondary">
                        Reconnect is not available for this source type.
                    </Typography>
                )}
            </Box>
        </div>
    );
};

export default ReconnectTab;
